import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowUpRight,
  ChevronRight,
  Hammer,
  MessagesSquare,
  Users,
} from 'lucide-react'
import {
  combineQueryStates,
  LinkChip,
  PageHeader,
  QueryErrorPanel,
  safeHttpUrl,
  SectionHeader,
  Skeleton,
  StatCard,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@loop3/ui'
import { formatShortDate, humanMonthLong } from '@loop3/dates'
import { currentMonth, currentTeam, type TeamMember } from '@loop3/universe'
import { reposForClient } from '@loop3/sync'
import { AskLupeButton } from '@/components/ask-lupe-button'
import { ClientJournal, CurrentWeekJournal } from '@/components/client-journal'
import { ClientSolutions } from '@/components/client-solutions'
import { useClient } from '@/hooks/useClient'
import {
  useClientBuilding,
  useClientMeetings,
  type ClientMeeting,
} from '@/hooks/useClientBusiness'
import { useClientJournal } from '@/hooks/useClientJournal'
import { useClientContacts } from '@/hooks/useClientContacts'
import { useClientSlackChannels } from '@/hooks/useClientSlackChannels'
import { useClientTeam } from '@/hooks/useClientTeam'
import { usePeople } from '@/hooks/usePeople'
import { useSolutionsLibrary } from '@/hooks/useSolutionsLibrary'
import { daysSince } from '@/lib/compute/client'
import { journalWindow } from '@/lib/compute/client-journal'
import { solutionsDelivered, weekActivity } from '@/lib/compute/client-signals'
import { solutionsLibraryHref } from '@/lib/compute/solutions-library'
import {
  CLIENT_TAB_LABELS,
  resolveClientTab,
  visibleClientTabs,
  type ClientTab,
} from '@/lib/compute/client-tabs'
import { REPOS } from '@/lib/distribution'
import { loop3AppUrl } from '@/lib/tools'
import type { Client, ClientContact, Person } from '@/lib/types'
import type { BuildingItem } from '@/lib/api/lupe'

const MEETINGS_URL = loop3AppUrl('meetings')

const TAB_CLASS =
  'inline-flex h-7 items-center rounded-md px-3 font-mono text-[11px] uppercase tracking-[0.08em] transition-colors text-muted-foreground hover:text-foreground data-[state=active]:bg-background data-[state=active]:text-[var(--l3-violet)] data-[state=active]:shadow-sm'

/** A section with data is hidden when empty; one still loading (no data, no error) keeps its tab. */
const isEmpty = (q: { data?: unknown[]; isError: boolean }) =>
  q.data ? q.data.length === 0 : q.isError

/** Business view of one client: health signals, what we built, recent meetings, people. */
export function ClientDetailPage() {
  const { prefix } = useParams<{ prefix: string }>()
  const client = useClient(prefix)
  const id = client.data?.id
  const contacts = useClientContacts(id)
  const slack = useClientSlackChannels(id)
  const people = usePeople()
  const team = useClientTeam(id)
  const meetings = useClientMeetings(id)
  const building = useClientBuilding(prefix)
  const journal = useClientJournal(id)
  const library = useSolutionsLibrary()
  const [params, setParams] = useSearchParams()
  const { status, retry } = combineQueryStates([client])

  if (status === 'error')
    return (
      <Page>
        <QueryErrorPanel onRetry={retry} />
      </Page>
    )
  if (status === 'pending') return <ClientSkeleton />
  if (!client.data)
    return (
      <Page>
        <div className="mt-4 text-sm opacity-70">Client not found.</div>
      </Page>
    )

  const c = client.data
  const firstSlackUrl = (slack.data ?? []).find((s) => s.url)?.url ?? null
  const peopleById = new Map((people.data ?? []).map((p) => [p.id, p]))
  const visible = visibleClientTabs({
    journal: isEmpty(journal),
    meetings: isEmpty({
      isError: meetings.isError,
      data: meetings.data?.recent,
    }),
  })
  const tab = resolveClientTab(params.get('tab'), visible)
  const setTab = (next: ClientTab) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        if (next === 'overview') p.delete('tab')
        else p.set('tab', next)
        return p
      },
      { replace: true }
    )

  return (
    <Page>
      <ClientHeader
        client={c}
        owner={peopleById.get(c.account_owner_id ?? '')}
        slackUrl={firstSlackUrl}
      />
      <Signals
        lastMeetingAt={meetings.data?.lastMeetingAt ?? null}
        prefix={c.prefix}
        solutions={
          library.data ? solutionsDelivered(library.data, c.prefix) : null
        }
        activity={
          journal.data?.length
            ? weekActivity(journal.data, journalWindow(new Date()).thisWeek)
            : null
        }
      />
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as ClientTab)}
        className="min-w-0"
      >
        <TabsList className="mb-8 h-auto flex-wrap justify-start gap-0.5 rounded-lg bg-muted/60 p-0.5">
          {visible.map((t) => (
            <TabsTrigger key={t} value={t} className={TAB_CLASS}>
              {CLIENT_TAB_LABELS[t]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-0 space-y-8">
          <BuildingSection items={building.data ?? []} />
          <CurrentWeekJournal
            clientId={c.id}
            onOpenJournal={() => setTab('journal')}
          />
          <ClientSolutions prefix={c.prefix} />
          <PeopleSection
            contacts={contacts.data ?? []}
            team={currentTeam(
              team.data ?? [],
              c.account_owner_id,
              currentMonth()
            )}
            peopleById={peopleById}
          />
          <Workspace
            client={c}
            repos={reposForClient(REPOS, c.prefix)}
            slack={slack.data ?? []}
          />
        </TabsContent>
        <TabsContent value="journal" className="mt-0">
          <ClientJournal clientId={c.id} />
        </TabsContent>
        <TabsContent value="meetings" className="mt-0">
          <ActivitySection
            meetings={meetings.data?.recent ?? []}
            prefix={c.prefix}
          />
        </TabsContent>
      </Tabs>
    </Page>
  )
}

function Page({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto max-w-7xl pb-16">{children}</div>
}

function ClientHeader({
  client: c,
  owner,
  slackUrl,
}: {
  client: Client
  owner: Person | undefined
  slackUrl: string | null
}) {
  return (
    <PageHeader
      className="mb-8 mt-2"
      title={c.name}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <AskLupeButton question={`Give me an overview of ${c.name}`} />
          {slackUrl && (
            <LinkChip href={slackUrl} service="slack" label="Slack" />
          )}
          {c.notion_url && (
            <LinkChip href={c.notion_url} service="notion" label="Notion" />
          )}
          {c.linear_team_url && (
            <LinkChip
              href={c.linear_team_url}
              service="linear"
              label="Linear"
            />
          )}
        </div>
      }
    >
      <dl className="mt-4 flex flex-wrap gap-x-10 gap-y-3">
        {c.since_month && (
          <Field label="Client since">
            {humanMonthLong(`${c.since_month}-01`)}
          </Field>
        )}
        {owner && <Field label="Account owner">{owner.name}</Field>}
      </dl>
    </PageHeader>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <dt className="eyebrow opacity-50">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  )
}

/** Signals the viewer is allowed to see; a card with no data is left out. */
function Signals({
  lastMeetingAt,
  prefix,
  solutions,
  activity,
}: {
  lastMeetingAt: string | null
  prefix: string
  solutions: number | null
  activity: { total: number; sub: string } | null
}) {
  const days = lastMeetingAt ? daysSince(lastMeetingAt, new Date()) : null

  const cards = [
    days !== null && {
      label: 'Last meeting',
      value: days === 0 ? 'Today' : `${days}d ago`,
    },
    solutions !== null && {
      label: 'Solutions delivered',
      value: String(solutions),
      to: solutionsLibraryHref({ type: 'solution', client: prefix }),
    },
    activity !== null && {
      label: 'Activity this week',
      value: String(activity.total),
      sub: activity.sub,
    },
  ].filter(Boolean) as {
    label: string
    value: string
    sub?: string
    to?: string
  }[]

  if (cards.length === 0) return null
  return (
    <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
      {cards.map(({ to, ...card }) =>
        to ? (
          <Link
            key={card.label}
            to={to}
            className="rounded-xl transition-colors hover:text-[var(--l3-violet)]"
          >
            <StatCard {...card} className="h-full" />
          </Link>
        ) : (
          <StatCard key={card.label} {...card} />
        )
      )}
    </div>
  )
}

const SOURCE_LABELS: Record<BuildingItem['sources'][number], string> = {
  kb: 'Knowledge base',
  linear: 'Linear',
  meetings: 'Meetings',
}

function BuildingSection({ items }: { items: BuildingItem[] }) {
  if (items.length === 0) return null
  return (
    <section>
      <SectionHeader icon={Hammer} label="What we're building" />
      <div className="border-t border-border">
        {items.map((item, i) => (
          <div
            key={`${i}-${item.title}`}
            className="border-b border-border/60 py-4"
          >
            <div className="text-sm font-medium">{item.title}</div>
            <p className="mt-1 text-sm leading-relaxed opacity-70">
              {item.summary}
            </p>
            <div className="mt-2 font-mono text-[0.6rem] uppercase tracking-[0.14em] opacity-40">
              {item.sources.map((src) => SOURCE_LABELS[src]).join(' · ')}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function ActivitySection({
  meetings,
  prefix,
}: {
  meetings: ClientMeeting[]
  prefix: string
}) {
  if (meetings.length === 0) return null
  return (
    <section>
      <SectionHeader
        icon={MessagesSquare}
        label="Recent activity"
        meta={
          <a
            href={`${MEETINGS_URL}/clients/${encodeURIComponent(prefix)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="eyebrow opacity-60 hover:text-[var(--l3-violet)] hover:opacity-100"
          >
            All meetings ↗
          </a>
        }
      />
      <div className="border-t border-border">
        {meetings.map((m) => (
          <MeetingRow key={m.id} meeting={m} />
        ))}
      </div>
    </section>
  )
}

function MeetingRow({ meeting: m }: { meeting: ClientMeeting }) {
  return (
    <a
      href={`${MEETINGS_URL}/meetings/${encodeURIComponent(m.id)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group grid grid-cols-[5.5rem_1fr_auto] gap-4 border-b border-border/60 py-4 transition-colors hover:bg-[var(--l3-violet-soft)]"
    >
      <span className="pl-1 font-mono text-xs opacity-50">
        {formatShortDate(new Date(m.starts_at))}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium group-hover:text-[var(--l3-violet)]">
          {m.title}
        </span>
        {m.tldr && (
          <span className="mt-1 line-clamp-2 block text-xs leading-relaxed opacity-60">
            {m.tldr}
          </span>
        )}
        {m.decisions.slice(0, 2).map((d, i) => (
          <span key={`${m.id}-${i}`} className="mt-1.5 block text-xs">
            <span className="text-[var(--l3-violet)]">Decided · </span>
            {d.text}
          </span>
        ))}
      </span>
      <ArrowUpRight className="mr-1 size-3.5 opacity-30 group-hover:opacity-80" />
    </a>
  )
}

function PeopleSection({
  contacts,
  team,
  peopleById,
}: {
  contacts: ClientContact[]
  team: TeamMember[]
  peopleById: Map<string, Person>
}) {
  if (contacts.length === 0 && team.length === 0) return null
  return (
    <section>
      <SectionHeader icon={Users} label="People" />
      <div className="grid grid-cols-1 gap-8 border-t border-border pt-4 md:grid-cols-2">
        <PeopleColumn title="Their team" empty="No contacts yet.">
          {contacts.map((p) => (
            <PersonLine
              key={p.id}
              name={p.name}
              detail={p.role}
              tag={p.is_primary ? 'Primary' : undefined}
              href={safeHttpUrl(p.linkedin_url)}
            />
          ))}
        </PeopleColumn>
        <PeopleColumn
          title="Our team"
          empty="No one is planned on this client this month."
        >
          {team.flatMap((t) => {
            const person = peopleById.get(t.personId)
            if (!person) return []
            return (
              <PersonLine
                key={t.personId}
                name={person.name}
                detail={teamDetail(t, person)}
                tag={t.isOwner ? 'Owner' : undefined}
              />
            )
          })}
        </PeopleColumn>
      </div>
    </section>
  )
}

/** Planned roles and start month, or the person's title when they have no plan here (owner only). */
function teamDetail(t: TeamMember, person: Person): string | null {
  if (!t.roles.length) return person.role
  const roles = t.roles.join(' · ')
  return t.since ? `${roles} · since ${humanMonthLong(`${t.since}-01`)}` : roles
}

function PeopleColumn({
  title,
  empty,
  children,
}: {
  title: string
  empty: string
  children: React.ReactNode[]
}) {
  return (
    <div>
      <h4 className="eyebrow mb-2 opacity-50">{title}</h4>
      {children.length > 0 ? (
        children
      ) : (
        <p className="text-sm opacity-50">{empty}</p>
      )}
    </div>
  )
}

function PersonLine({
  name,
  detail,
  tag,
  href,
}: {
  name: string
  detail?: string | null
  tag?: string
  href?: string | null
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-2.5">
      <div className="min-w-0">
        <span className="text-sm">{name}</span>
        {tag && (
          <span className="ml-2 font-mono text-[10px] uppercase text-[var(--l3-violet)]">
            {tag}
          </span>
        )}
        {detail && <div className="text-xs opacity-60">{detail}</div>}
      </div>
      {href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-xs opacity-50 hover:opacity-100"
        >
          LinkedIn ↗
        </a>
      )}
    </div>
  )
}

/** Repos, Slack channels and GitHub, collapsed: useful, but not what the page is about. */
function Workspace({
  client: c,
  repos,
  slack,
}: {
  client: Client
  repos: ReturnType<typeof reposForClient>
  slack: { id: string; name: string; url: string | null }[]
}) {
  const summary = [
    repos.length > 0 && `${repos.length} repos`,
    slack.length > 0 && `${slack.length} Slack channels`,
  ].filter(Boolean)
  return (
    <details className="group border-t border-border pt-4">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-sm opacity-60 hover:opacity-100">
        <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" />
        <span className="eyebrow">Workspace</span>
        {summary.length > 0 && (
          <span className="text-xs">· {summary.join(' · ')}</span>
        )}
      </summary>
      <div className="mt-4 grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <h4 className="eyebrow mb-2 opacity-50">Repos</h4>
          {repos.map((r) => (
            <a
              key={r.repo}
              href={`https://github.com/${r.repo}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block truncate py-1 font-mono text-xs opacity-70 hover:text-[var(--l3-violet)] hover:opacity-100"
            >
              {r.repo}
            </a>
          ))}
          {c.github_org_url && (
            <LinkChip
              href={c.github_org_url}
              service="github"
              label="GitHub org"
            />
          )}
        </div>
        <div>
          <h4 className="eyebrow mb-2 opacity-50">Slack channels</h4>
          {slack.map((s) => {
            const url = safeHttpUrl(s.url)
            return url ? (
              <a
                key={s.id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block py-1 font-mono text-xs opacity-70 hover:text-[var(--l3-violet)] hover:opacity-100"
              >
                #{s.name}
              </a>
            ) : (
              <div key={s.id} className="py-1 font-mono text-xs opacity-70">
                #{s.name}
              </div>
            )
          })}
        </div>
      </div>
    </details>
  )
}

function ClientSkeleton() {
  return (
    <Page>
      <div className="mb-8 mt-2 border-b border-border pb-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-3 h-3 w-72 opacity-60" />
      </div>
      <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-20 rounded-lg" />
        ))}
      </div>
      <Skeleton className="mb-3 h-3 w-28" />
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14 rounded-lg" />
        ))}
      </div>
    </Page>
  )
}
